import _ from 'lodash'
import { IDFPoint, IDataFrame } from '../definitions/DataAnalytics'
import { IKPICard } from '../definitions/Shared'
import Helper from '../helpers/Helper'

class DataframeManagerClass {
    create = (points: IDFPoint[], options?: { noDelta?: boolean }): IDataFrame => {
        let delta, deltaPercentage
        const myPoints = points

        if (!options?.noDelta) {
            const firstValue = _.first(myPoints)?.y ?? 0
            const lastValue = _.last(myPoints)?.y ?? 0

            delta = lastValue - firstValue
            deltaPercentage = firstValue !== 0 ? (delta / firstValue) * 100 : 0
        }

        return { points: myPoints, delta, deltaPercentage }
    }

    getMinDate(points: IDFPoint[]): Date {
        return points.reduce((min, point) => (new Date(point.x) < min ? new Date(point.x) : min), new Date(points[0].x))
    }

    getMaxDate(points: IDFPoint[]): Date {
        return points.reduce((max, point) => (new Date(point.x) > max ? new Date(point.x) : max), new Date(points[0].x))
    }

    delta(points: IDFPoint[]): IDataFrame {
        const firstValue = _.first(points)?.y ?? 0
        const lastValue = _.last(points)?.y ?? 0

        const delta = lastValue - firstValue
        const deltaPercentage = firstValue !== 0 ? (delta / firstValue) * 100 : 0

        return {
            points: points,
            delta: delta,
            deltaPercentage: deltaPercentage
        }
    }

    withDelta = (df: IDataFrame): IDataFrame => {
        return this.delta(df.points)
    }

    createKPICard(dataFrame: IDataFrame): IKPICard {
        return {
            value: _.last(dataFrame.points)?.y ?? 0,
            df: dataFrame
        }
    }

    average = (df: IDataFrame) => _.mean(df?.points.map(x => x.y) ?? [])

    sumAll = (df: IDataFrame) => _.round(_.sum(df?.points.map(x => x.y) ?? []), 2)

    sum = (dfs: IDataFrame[]) => {
        if (!dfs || dfs.length === 0)
            return null

        const firstDataFrame = dfs[0]
        if (dfs.some(x => x.points.length !== firstDataFrame.points.length))
            return null

        const resultDataFrame: IDataFrame = { points: [] }
        for (const point of firstDataFrame.points) {
            const { x } = point
            let sumY = point.y

            for (let i = 1; i < dfs.length; i++) {
                const currentDataFrame = dfs[i]
                const currentPoint = currentDataFrame.points.find(p => p.x === x)
                if (currentPoint)
                    sumY += currentPoint.y
            }
            resultDataFrame.points.push({ x, y: sumY })
        }

        return resultDataFrame
    }

    subtract = (df1: IDataFrame, df2: IDataFrame) => {
        if (df1.points.length !== df2.points.length) throw new Error('Dataframe.subtract() not same length.')

        const result: IDataFrame = { points: [] }
        for (let i = 0; i < df1.points.length; i++) {
            const p1 = df1.points[i]
            const p2 = df2.points[i]

            const y = p1.y - p2.y

            result.points.push({ x: p1.x, y: y })
        }

        return this.withDelta(result)
    }

    divide = (df1: IDataFrame, df2: IDataFrame) => {
        if (df1.points.length !== df2.points.length) throw new Error('Dataframe.divide() not same length.')

        const result: IDataFrame = { points: [] }
        for (let i = 0; i < df1.points.length; i++) {
            const p1 = df1.points[i]
            const p2 = df2.points[i]

            const y = (Helper.hasVal(p2.y) && p2.y !== 0) ? p1.y / p2.y : null

            result.points.push({ x: p1.x, y: y })
        }

        return this.withDelta(result)
    }

    multiply = (df1: IDataFrame, df2: IDataFrame) => {
        if (df1.points.length !== df2.points.length) throw new Error('Dataframe.multiply() not same length.')

        const result: IDataFrame = { points: [] }
        for (let i = 0; i < df1.points.length; i++) {
            const p1 = df1.points[i]
            const p2 = df2.points[i]

            const y = p1.y * p2.y

            result.points.push({ x: p1.x, y: y })
        }

        return this.withDelta(result)
    }

    multiplyBy = (df: IDataFrame, val: number) => {
        const result: IDataFrame = { points: [] }
        for (let i = 0; i < df.points.length; i++) {
            const p1 = df.points[i]

            const y = p1.y * val

            result.points.push({ x: p1.x, y: y })
        }

        return this.withDelta(result)
    }

    percentage = (df1: IDataFrame, df2: IDataFrame) => {
        if (df1.points.length !== df2.points.length) throw new Error('Dataframe.percentage() not same length.')

        const result: IDataFrame = { points: [] }
        for (let i = 0; i < df1.points.length; i++) {
            const p1 = df1.points[i]
            const p2 = df2.points[i]

            const y = _.round(p1.y * p2.y / 100, 2)

            result.points.push({ x: p1.x, y: y })
        }

        return this.withDelta(result)
    }

    /**
     * Return the value from df1 as long as it is less than the value form df2 (max each point value)
     */
    capAt = (df1: IDataFrame, df2: IDataFrame) => {
        if (df1.points.length !== df2.points.length) throw new Error('Dataframe.capAt() not same length.')

        const result: IDataFrame = { points: [] }
        for (let i = 0; i < df1.points.length; i++) {
            const p1 = df1.points[i]
            const p2 = df2.points[i]

            const y = _.min([p1.y, p2.y])

            result.points.push({ x: p1.x, y: y })
        }

        return this.withDelta(result)
    }

    avg = (dfs: IDataFrame[]) => {
        if (!dfs || dfs.length === 0)
            return null

        const firstDataFrame = dfs[0]
        if (dfs.some(x => x.points.length !== firstDataFrame.points.length))
            return null

        const resultDataFrame: IDataFrame = { points: [] }
        for (const point of firstDataFrame.points) {
            const { x } = point
            let sumY = point.y

            for (let i = 1; i < dfs.length; i++) {
                const currentDataFrame = dfs[i]
                const currentPoint = currentDataFrame.points.find(p => p.x === x)
                if (currentPoint)
                    sumY += currentPoint.y
            }
            resultDataFrame.points.push({ x, y: sumY / dfs.length })
        }

        return resultDataFrame
    }

    getYs = (df: IDataFrame) => df?.points?.map(p => p.y) ?? []
    getXs = (df: IDataFrame) => df?.points?.map(p => p.x) ?? []
}

const DataframeManager = new DataframeManagerClass()
export default DataframeManager
